/**
 * What this computer has installed, told to Vephus (docs/refactor/14-skills-e-mcps.md): the skills
 * (the person's own, and each open project's) and the MCP servers, by name, on or off — never a
 * configuration or a secret. The web lists it per computer in Agentes › Skills e MCPs and can send an
 * install to one of them.
 *
 * Told a little after the app opens (the server knows a computer from its first asking for work), every
 * ten minutes, and right after a skill or an MCP server is switched or added here.
 */

import { agentFetch, agentKeyRef, isDesktop, mcpList, skillsList } from './desktop'
import { agentProgram, detectAgents, type Permission } from './localAgent'
import { readRunnerSettings } from './runnerHost'
import { API_BASE } from './api'
import { allFolders } from './repoFolders'

const MACHINE_KEY = 'devpulse.machineId'
// Also the app's sign of life for the web's "PC de casa ligado" (docs/refactor/16): at least every 5 min.
const EVERY_MS = 5 * 60_000
const FIRST_AFTER_MS = 20_000

export interface InventoryItem {
  name: string
  on: boolean
  scope?: 'user' | 'project'
}

/**
 * The skills and MCP servers here, by name: a user skill once, each project's under it; the agents it can run
 * ("Quem faz?"); and what the agent may do here without asking (docs/refactor/59), so the web can say what a
 * request sent to this computer can count on.
 */
export async function readInventory(projectFolders: string[]): Promise<{ skills: InventoryItem[]; mcps: InventoryItem[]; agents: string[]; permission: Permission }> {
  const seen = new Set<string>()
  const skills: InventoryItem[] = []
  for (const folder of [null, ...projectFolders]) {
    for (const skill of await skillsList(folder).catch(() => [])) {
      const key = `${skill.scope}:${skill.name}`
      if (seen.has(key)) continue
      seen.add(key)
      skills.push({ name: skill.name, on: skill.enabled, scope: skill.scope })
    }
  }
  const mcps = (await mcpList().catch(() => [])).map((server) => ({ name: server.name, on: server.enabled }))
  // The second Claude account is Claude Code to the web (docs/refactor/60): one "claude", never a second agent.
  const agents = [...new Set((await detectAgents().catch(() => [])).filter((agent) => agent.runnable).map((agent) => agentProgram(agent.id)))]
  return { skills, mcps, agents, permission: readRunnerSettings().permission }
}

/** Tells Vephus what is installed here. False when it could not (no key yet, the computer unknown so far). */
export async function reportInventory(projectFolders: string[]): Promise<boolean> {
  if (!isDesktop()) return false
  const key = await agentKeyRef()
  let machine: string | null = null
  try {
    machine = localStorage.getItem(MACHINE_KEY)
  } catch {
    machine = null
  }
  if (!key || !machine) return false
  const inventory = await readInventory(projectFolders)
  try {
    const response = await agentFetch(`${API_BASE}/prompt-queue/machines/${encodeURIComponent(machine)}/inventory`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-DevPulse-Key': key },
      body: JSON.stringify(inventory),
    })
    return response.ok
  } catch {
    return false
  }
}

let timer: number | null = null

/** Starts telling, now-ish and every ten minutes. Returns the stop. */
export function startInventoryReports(projectFolders: () => string[]): () => void {
  if (!isDesktop() || timer !== null) return () => undefined
  const tell = () => void reportInventory(projectFolders())
  const first = window.setTimeout(tell, FIRST_AFTER_MS)
  timer = window.setInterval(tell, EVERY_MS)
  return () => {
    window.clearTimeout(first)
    if (timer !== null) window.clearInterval(timer)
    timer = null
  }
}

let soon: number | null = null

/** A skill or an MCP server was switched or added here: told in a moment (several changes, once). */
export function inventoryChanged(): void {
  if (!isDesktop()) return
  if (soon !== null) window.clearTimeout(soon)
  soon = window.setTimeout(() => {
    soon = null
    void reportInventory(allFolders())
  }, 1500)
}
